import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import { isAbortError } from "../../utils/common";
export interface ReturnTransaction {
  OrderParentId: number;
  TransactionId: number;
  TransactionReturnId: number;
  ReturnTransactionStatus: string;
  OrderSkuId: number;
  Description: string;
  EngagementId: string;
  EngagementName: string;
  SkuId: string;
  SkuName: string;
  ProductId: string;
  ProductName: string;
  SkuType: string;
  CostIntervalCd: string;
  UnitCost: number;
  Intervals: number;
  IntervalNum: number;
  StatusCd: string;
  OrgQuantity: number;
  OrgTotalCost: number;
  TransactionDtm: string;
  IntervalStartDtm: string;
  IntervalEndDtm: string;
  CostMetric: string;
  CostMetricCd: string;
  ReturnQuantity: number;
  ReturnCost: number;
  ReturnReason: string;
  ApprovedQuantity: number;
  ApprovedCost: number;
  RejectReason: string | null;
  ReturnRequestDate: string;
}

interface PreviousReturnListState {
  isLoading: boolean;
  data: ReturnTransaction[] | null;
  error: string | null;
}

const initialState: PreviousReturnListState = {
  isLoading: true,
  data: null,
  error: null,
};

export interface RequestObject {
  Action: string;
  OrderSkuId?: number;
}

export interface ResponseError {
  message: string;
}

export const fetchPreviousReturnListData = createAsyncThunk<
  any,
  RequestObject,
  { rejectValue: ResponseError }
>(
  "PreviousReturnSlice/fetchPreviousReturnListData",
  async (requestObject, thunkApi) => {
    try {
      const response = await httpClient.post("/order", requestObject);

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to fetch order list",
          });
        }
      }
      return response.data;
    } catch (error: any) {
      if (isAbortError(error) || error?.code === "ERR_CANCELED") {
        // Handle abort
        return;
      }
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to fetch order list",
      });
    }
  },
);

const PreviousReturnSlice = createSlice({
  name: "PreviousReturnSlice",
  initialState,
  reducers: {
    resetOrderPipelineListState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreviousReturnListData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchPreviousReturnListData.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (
            action?.payload?.Status == "success" &&
            action?.payload !== undefined
          ) {
            state.isLoading = false;
            state.data = action.payload.Data.Result;
            state.error = null;
          } else if (action?.payload?.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        fetchPreviousReturnListData.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error =
            action.payload.message || "Failed to fetch previous return list";
        },
      );
  },
});

export const { resetOrderPipelineListState } = PreviousReturnSlice.actions;

export default PreviousReturnSlice.reducer;
